import React, { useState, useEffect, useRef } from 'react';
import { authenticatedFetch } from '../../lib/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { 
  UserPlusIcon, 
  PencilIcon, 
  TrashIcon,
  ShieldCheckIcon,
  UserIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface UserRole {
  id: string;
  user_id: string;
  email: string;
  role: 'admin' | 'operator' | 'customer' | 'partner';
  customer_id?: string;
  partner_id?: string;
  status: 'active' | 'inactive' | 'suspended';
  assigned_customer_ids?: string[]; // 營運人員指派的客戶 ID 列表
  customer_company?: string;
  partner_name?: string;
  created_at: string;
  updated_at: string;
}

interface Customer {
  id: string;
  company: string;
}

interface Partner {
  id: string;
  name: string;
}

const UserManagement: React.FC = () => {
  const { user, userRole, loading: authLoading, authInitialized } = useAuth();
  const [users, setUsers] = useState<UserRole[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 使用 ref 追蹤是否已經載入過資料，避免重複載入
  const hasLoadedRef = useRef(false);
  
  // 篩選狀態
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // 模態框狀態
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRole | null>(null);
  
  // 表單狀態
  const [formData, setFormData] = useState({
    email: '',
    role: 'operator' as 'admin' | 'operator' | 'customer' | 'partner',
    customer_id: '',
    partner_id: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
    assigned_customer_ids: [] as string[]
  });

  // 當認證完成後，載入資料（只執行一次）
  useEffect(() => {
    // 等待認證初始化完成
    if (!authInitialized || authLoading) {
      // eslint-disable-next-line no-console
      return;
    }
    
    // 如果沒有使用者或角色，不載入資料
    if (!user || !userRole) {
      // eslint-disable-next-line no-console
      setLoading(false);
      return;
    }
    
    // 如果已經載入過，不再重複載入
    if (hasLoadedRef.current) {
      // eslint-disable-next-line no-console
      return;
    }
    
    // 標記為已載入
    hasLoadedRef.current = true;
    
    // eslint-disable-next-line no-console
    // 認證完成後載入資料
    loadUsers();
    loadCustomers();
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, authLoading, user, userRole]);
  
  // 當篩選條件變化時，重新載入使用者列表（客戶和夥伴列表不需要重新載入）
  useEffect(() => {
    // 只有在認證完成且用戶和角色都存在時才執行
    if (!authInitialized || authLoading || !user || !userRole) {
      return;
    }
    
    // 如果還沒有初始載入，不執行
    if (!hasLoadedRef.current) {
      return;
    }
    
    // 只重新載入使用者列表（篩選條件只影響使用者列表）
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await authenticatedFetch(`/api/users?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
        setError(null);
      } else {
        setError(data.error || '載入使用者失敗');
      }
    } catch {
      setError('載入使用者失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await authenticatedFetch('/api/db/query', {
        method: 'POST',
        body: JSON.stringify({
          table: 'customers',
          select: ['id', 'company'],
          orderBy: [{ field: 'company', direction: 'ASC' }]
        })
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(data.data);
      }
    } catch {
      // 載入客戶列表失敗，靜默處理
    }
  };

  const loadPartners = async () => {
    try {
      const response = await authenticatedFetch('/api/db/query', {
        method: 'POST',
        body: JSON.stringify({
          table: 'partners',
          select: ['id', 'name'],
          orderBy: [{ field: 'name', direction: 'ASC' }]
        })
      });
      const data = await response.json();
      if (data.success) {
        setPartners(data.data);
      }
    } catch {
      // 載入夥伴列表失敗，靜默處理
    }
  };

  // 邀請使用者
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await authenticatedFetch('/api/users/invite', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('使用者邀請成功！');
        setShowInviteModal(false);
        resetForm();
        loadUsers();
      } else {
        const errMsg = [data.error, data.message].filter(Boolean).join('：');
        alert(`邀請失敗: ${errMsg || '請稍後再試'}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      alert(msg ? `邀請失敗: ${msg}` : '邀請失敗，請檢查網路連線');
    }
  };

  // 更新使用者
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUser) return;
    
    try {
      const response = await authenticatedFetch(`/api/users/${selectedUser.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          role: formData.role,
          status: formData.status,
          customer_id: formData.customer_id || null,
          partner_id: formData.partner_id || null,
          assigned_customer_ids: formData.role === 'operator' ? formData.assigned_customer_ids : undefined
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('使用者更新成功！');
        setShowEditModal(false);
        setSelectedUser(null);
        resetForm();
        loadUsers();
      } else {
        alert(`更新失敗: ${data.error || data.message}`);
      }
    } catch {
      alert('更新失敗');
    }
  };

  // 刪除使用者
  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await authenticatedFetch(`/api/users/${selectedUser.id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('使用者刪除成功！');
        setShowDeleteModal(false);
        setSelectedUser(null);
        loadUsers();
      } else {
        alert(`刪除失敗: ${data.error || data.message}`);
      }
    } catch {
      alert('刪除失敗');
    }
  };

  // 重置表單
  const resetForm = () => {
    setFormData({
      email: '',
      role: 'operator',
      customer_id: '',
      partner_id: '',
      status: 'active',
      assigned_customer_ids: []
    });
  };

  // 開啟編輯模態框
  const openEditModal = (user: UserRole) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      role: user.role,
      customer_id: user.customer_id || '',
      partner_id: user.partner_id || '',
      status: user.status,
      assigned_customer_ids: user.assigned_customer_ids || []
    });
    setShowEditModal(true);
  };

  // 開啟刪除確認
  const openDeleteModal = (user: UserRole) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // 篩選使用者
  const filteredUsers = users.filter(user => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return user.email.toLowerCase().includes(query) ||
             user.customer_company?.toLowerCase().includes(query) ||
             user.partner_name?.toLowerCase().includes(query);
    }
    return true;
  });

  // 角色名稱
  const roleNames: Record<string, string> = {
    admin: '系統管理員',
    operator: '營運人員',
    customer: '客戶',
    partner: '承攬夥伴'
  };

  // 狀態名稱
  const statusNames: Record<string, string> = {
    active: '啟用',
    inactive: '停用',
    suspended: '暫停'
  };

  // 統計數據
  const stats = {
    total: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    operator: users.filter(u => u.role === 'operator').length,
    customer: users.filter(u => u.role === 'customer').length
  };

  return (
    <div className="p-6">
      {/* 標題 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <ShieldCheckIcon className="w-8 h-8 mr-2 text-blue-600" />
          使用者權限管理
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          管理系統使用者的角色和權限
        </p>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">總使用者</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <div className="text-sm text-blue-600">管理員</div>
          <div className="text-2xl font-bold text-blue-900">{stats.admin}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-sm text-green-600">營運人員</div>
          <div className="text-2xl font-bold text-green-900">{stats.operator}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg shadow">
          <div className="text-sm text-purple-600">客戶</div>
          <div className="text-2xl font-bold text-purple-900">{stats.customer}</div>
        </div>
      </div>

      {/* 操作列 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* 搜尋 */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="搜尋郵件、客戶或承攬夥伴..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
            />
          </div>

          {/* 篩選 */}
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">全部角色</option>
              <option value="admin">系統管理員</option>
              <option value="operator">營運人員</option>
              <option value="customer">客戶</option>
              <option value="partner">承攬夥伴</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">全部狀態</option>
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
              <option value="suspended">暫停</option>
            </select>
          </div>

          {/* 新增按鈕 */}
          <button
            onClick={() => {
              resetForm();
              setShowInviteModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
          >
            <UserPlusIcon className="w-5 h-5" />
            邀請使用者
          </button>
        </div>
      </div>

      {/* 錯誤訊息 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* 使用者列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">載入中...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>沒有找到使用者</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  使用者
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  角色
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  關聯資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  建立時間
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.email}
                        </div>
                        {user.user_id === 'PENDING' && (
                          <div className="text-xs text-yellow-600 flex items-center mt-1">
                            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                            待首次登入
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                      user.role === 'operator' ? 'bg-green-100 text-green-800' :
                      user.role === 'customer' ? 'bg-purple-100 text-purple-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {roleNames[user.role]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.role === 'operator' && user.assigned_customer_ids && user.assigned_customer_ids.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {(user.assigned_customer_ids || []).slice(0, 3).map((cid) => {
                          const c = customers.find(x => x.id === cid);
                          return c ? <span key={cid} className="flex items-center"><BuildingOfficeIcon className="w-4 h-4 mr-1 flex-shrink-0" />{c.company}</span> : null;
                        })}
                        {(user.assigned_customer_ids?.length || 0) > 3 && (
                          <span className="text-xs text-gray-400">等共 {user.assigned_customer_ids.length} 位</span>
                        )}
                      </div>
                    ) : user.customer_company ? (
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 mr-1" />
                        {user.customer_company}
                      </div>
                    ) : user.partner_name ? (
                      <div className="flex items-center">
                        <UserIcon className="w-4 h-4 mr-1" />
                        {user.partner_name}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center gap-1 w-fit ${
                      user.status === 'active' ? 'bg-green-100 text-green-800' :
                      user.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {user.status === 'active' ? (
                        <CheckCircleIcon className="w-3 h-3" />
                      ) : (
                        <XCircleIcon className="w-3 h-3" />
                      )}
                      {statusNames[user.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('zh-TW')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="編輯"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="text-red-600 hover:text-red-900"
                      title="刪除"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 邀請使用者模態框 */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <UserPlusIcon className="w-6 h-6 mr-2 text-blue-600" />
                邀請使用者
              </h3>
            </div>

            <form onSubmit={handleInvite}>
              {/* 郵件 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  郵件地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="user@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  使用者需使用此 Google 帳號登入
                </p>
              </div>

              {/* 角色 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  角色 <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    role: e.target.value as 'admin' | 'operator' | 'customer' | 'partner',
                    customer_id: '',
                    partner_id: '',
                    assigned_customer_ids: e.target.value === 'operator' ? formData.assigned_customer_ids : []
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="operator">營運人員</option>
                  <option value="admin">系統管理員</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  僅可邀請內部人員。客戶與承攬夥伴無法登入本系統。
                </p>
              </div>

              {/* 客戶指派（僅限營運人員） */}
              {formData.role === 'operator' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    可存取客戶 <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">該營運人員僅能看到以下客戶的相關資訊</p>
                  <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    {customers.map((customer) => (
                      <div key={customer.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          id={`invite-cust-${customer.id}`}
                          checked={formData.assigned_customer_ids.includes(customer.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                assigned_customer_ids: [...formData.assigned_customer_ids, customer.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                assigned_customer_ids: formData.assigned_customer_ids.filter(id => id !== customer.id)
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`invite-cust-${customer.id}`} className="text-sm text-gray-700 cursor-pointer">
                          {customer.company}
                        </label>
                      </div>
                    ))}
                  </div>
                  {formData.assigned_customer_ids.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      已指派 {formData.assigned_customer_ids.length} 位客戶
                    </p>
                  )}
                </div>
              )}

              {/* 按鈕 */}
              <div className="flex gap-2 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  邀請
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 編輯使用者模態框 */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <PencilIcon className="w-6 h-6 mr-2 text-blue-600" />
                編輯使用者
              </h3>
              <p className="text-sm text-gray-500 mt-1">{selectedUser.email}</p>
            </div>

            <form onSubmit={handleUpdate}>
              {/* 角色 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  角色 <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value as 'admin' | 'operator' | 'customer' | 'partner';
                    setFormData({ 
                      ...formData, 
                      role: newRole,
                      customer_id: '',
                      partner_id: '',
                      assigned_customer_ids: newRole === 'operator' ? formData.assigned_customer_ids : []
                    });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="operator">營運人員</option>
                  <option value="admin">系統管理員</option>
                  {(formData.role === 'customer' || formData.role === 'partner') && (
                    <option value={formData.role}>
                      {formData.role === 'customer' ? '客戶（無法登入）' : '承攬夥伴（無法登入）'}
                    </option>
                  )}
                </select>
                {(formData.role === 'customer' || formData.role === 'partner') && (
                  <p className="mt-1 text-xs text-red-600">
                    此角色無法登入系統。建議改為營運人員／系統管理員，或刪除此帳號。
                  </p>
                )}
              </div>

              {/* 客戶選擇 */}
              {formData.role === 'customer' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    關聯客戶 <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">請選擇客戶</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 承攬夥伴選擇 */}
              {formData.role === 'partner' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    關聯承攬夥伴 <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.partner_id}
                    onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">請選擇承攬夥伴</option>
                    {partners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 客戶指派（僅限營運人員） */}
              {formData.role === 'operator' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    可存取客戶 <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">該營運人員僅能看到以下客戶的相關資訊</p>
                  <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    {customers.map((customer) => (
                      <div key={customer.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          id={`cust-${customer.id}`}
                          checked={formData.assigned_customer_ids.includes(customer.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                assigned_customer_ids: [...formData.assigned_customer_ids, customer.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                assigned_customer_ids: formData.assigned_customer_ids.filter(id => id !== customer.id)
                              });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`cust-${customer.id}`} className="text-sm text-gray-700 cursor-pointer">
                          {customer.company}
                        </label>
                      </div>
                    ))}
                  </div>
                  {formData.assigned_customer_ids.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      已指派 {formData.assigned_customer_ids.length} 位客戶
                    </p>
                  )}
                </div>
              )}

              {/* 狀態 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  狀態
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'suspended' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                  <option value="suspended">暫停</option>
                </select>
              </div>

              {/* 按鈕 */}
              <div className="flex gap-2 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 刪除確認模態框 */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center text-red-600">
                <TrashIcon className="w-6 h-6 mr-2" />
                刪除使用者
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">確定要刪除以下使用者嗎？</p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{selectedUser.email}</p>
                <p className="text-sm text-gray-500">角色: {roleNames[selectedUser.role]}</p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ 此操作無法復原，使用者的所有專案權限也會被移除。
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                確定刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

